/**
 * DOM操作工具函数
 * 统一管理DOM相关的操作逻辑
 */

/**
 * 滚动到指定元素位置
 * 用于表单校验失败时，自动滚动到错误字段位置
 * @param {string} elementId - 目标元素的ID
 * @param {number} offset - 滚动偏移量（像素），默认144
 * @param {number} delay - 延迟执行时间（毫秒），默认100
 * @example
 * scrollToElement('softwareName', 144, 100)
 */
export const scrollToElement = (elementId, offset = 144, delay = 100) => {
  setTimeout(() => {
    // 获取滚动容器
    const div = document.getElementById("form-center");
    // 获取目标元素
    const messageBox = document.getElementById(elementId);

    // 容错处理：如果元素不存在，直接返回
    if (!div || !messageBox) {
      console.warn(`元素未找到: ${elementId}`);
      return;
    }

    // 计算滚动位置（目标元素顶部 - 偏移量）
    const scrollTop = messageBox.offsetTop - offset;

    // 滚动容器
    div.scrollTop = scrollTop;

    // 同时滚动页面主体
    document.documentElement.scrollTop = scrollTop;
  }, delay);
};

/**
 * 在新窗口中打开指定路径
 * @param {string} path - 路由路径（hash模式）
 * @example
 * openWindow('#/applicant') // 打开著作权人库页面
 */
export const openWindow = (path) => {
  // 获取当前域名
  const baseUrl = window.location.origin;
  // 分割当前路径
  const pathArr = window.location.pathname.split('/');
  // 替换最后一部分为新路径
  pathArr.splice(pathArr.length - 1, 1, path);
  // 拼接完整URL
  const url = baseUrl + pathArr.join('/');
  // 在新标签页打开
  window.open(url, "_blank");
};

/**
 * 高亮文本中的关键词（用于搜索结果展示）
 * 将匹配的关键词用特殊标记包裹，供前端渲染高亮样式
 * @param {string} text - 原始文本
 * @param {string} keyword - 要高亮的关键词
 * @returns {Array<{text: string, highlight: boolean}>} 文本片段数组
 * @example
 * getHighlightSegments('北京天华科技有限公司', '天华')
 * // 返回: [
 * //   { text: '北京', highlight: false },
 * //   { text: '天华', highlight: true },
 * //   { text: '科技有限公司', highlight: false }
 * // ]
 */
export const getHighlightSegments = (text, keyword) => {
  // 如果关键词为空，直接返回整个文本
  if (!keyword || !keyword.trim()) {
    return [{ text, highlight: false }];
  }

  const segments = [];
  const lowerText = text.toLowerCase();
  const lowerKeyword = keyword.toLowerCase();
  let lastIndex = 0;

  // 查找所有匹配位置
  let index = lowerText.indexOf(lowerKeyword, lastIndex);

  while (index !== -1) {
    // 添加匹配前的普通文本
    if (index > lastIndex) {
      segments.push({
        text: text.substring(lastIndex, index),
        highlight: false
      });
    }

    // 添加高亮文本
    segments.push({
      text: text.substring(index, index + keyword.length),
      highlight: true
    });

    lastIndex = index + keyword.length;
    index = lowerText.indexOf(lowerKeyword, lastIndex);
  }

  // 添加剩余的普通文本
  if (lastIndex < text.length) {
    segments.push({
      text: text.substring(lastIndex),
      highlight: false
    });
  }

  return segments;
};

/**
 * 设置driver.js引导弹窗的位置
 * @param {string} selector - CSS选择器
 * @param {Object} position - 位置配置 {top, left, right, bottom}
 * @example
 * setDriverPopoverPosition('#driver-popover-content', { top: '20px', left: '222px' })
 */
export const setDriverPopoverPosition = (selector, position) => {
  const element = document.querySelector(selector);
  if (!element) {
    console.warn(`元素未找到: ${selector}`);
    return;
  }

  // 构建inset样式值
  const inset = [
    position.top || 'auto',
    position.right || 'auto',
    position.bottom || 'auto',
    position.left || 'auto'
  ].join(' ');

  element.style.inset = inset;
};

/**
 * 平滑滚动到页面顶部
 * @param {number} duration - 滚动持续时间（毫秒），默认300
 * @example
 * smoothScrollToTop(300)
 */
export const smoothScrollToTop = (duration = 300) => {
  const start = window.pageYOffset;
  const startTime = performance.now();

  const scroll = (currentTime) => {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);

    // 使用缓动函数（easeInOutQuad）
    const ease = progress < 0.5
      ? 2 * progress * progress
      : -1 + (4 - 2 * progress) * progress;

    window.scrollTo(0, start * (1 - ease));

    if (progress < 1) {
      requestAnimationFrame(scroll);
    }
  };

  requestAnimationFrame(scroll);
};

/**
 * 检查元素是否在视口中
 * @param {HTMLElement} element - DOM元素
 * @returns {boolean} 是否在视口中
 * @example
 * const isVisible = isElementInViewport(document.getElementById('myElement'))
 */
export const isElementInViewport = (element) => {
  if (!element) return false;

  const rect = element.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
};

/**
 * 获取元素相对于文档的偏移量
 * @param {HTMLElement} element - DOM元素
 * @returns {{top: number, left: number}} 偏移量对象
 * @example
 * const offset = getElementOffset(document.getElementById('myElement'))
 * console.log(offset.top, offset.left)
 */
export const getElementOffset = (element) => {
  if (!element) {
    return { top: 0, left: 0 };
  }

  const rect = element.getBoundingClientRect();
  const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

  return {
    top: rect.top + scrollTop,
    left: rect.left + scrollLeft
  };
};
